<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed, ref } from 'vue'
import {
  GraphEntityType,
  Mutation,
  PermissionOrganization,
  SetTreeGraph,
  SetTreeGraphNode,
  StepTreeDataQuery,
  StepTreeDataQueryVariables,
} from '@/generated/graphql'
import { useMutation, useQuery } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import StepTreeNode from '@/components/step/StepTreeNode.vue'
import CreateSectionDialog from '@/components/step/CreateSectionDialog.vue'
import StepDropDecisionDialog, { DropDecision } from '@/components/step/StepDropDecisionDialog.vue'
import { associatedIssueTableIssueLinkFragment } from '@/components/issue/issueFragments'
import useAuthorization from '@/composables/useAuthorization'
import { isBopLocked } from '@/utils/bopLock'
import useCreateStepNode from '@/composables/useCreateStepNode'
import { ComponentExposed } from 'vue-component-type-helpers'

const props = defineProps<{
  bopId: string
  moduleId: string
  productId: string
  selectedNodeId?: string
}>()
const emit = defineEmits<{
  createNode: [parentNodeId?: string, afterNodeId?: string]
  copyNode: []
  addFromLibrary: []
  proposeFromMedia: []
  preview: []
  selectNodeId: [value: string]
}>()

const { t } = useI18n()
const { hasOrgAuthz } = useAuthorization()

type LocalModule = StepTreeDataQuery['product']['module']
type LocalNode = LocalModule['nodes'][0]
export type StepTreeStepNode = {
  numbering: string
  isSelected: boolean
  ancestryNodeIds: string[]
  stepNode: LocalNode
  children: StepTreeStepNode[]
}

const fetchQuery = useQuery<StepTreeDataQuery, StepTreeDataQueryVariables>(
  gql`
    query StepTreeData($bopId: ID!, $moduleId: ID!, $includeIssues: Boolean!) {
      product {
        bop(id: $bopId) {
          id
          versionStatus
          productConfiguration {
            id
            archived
          }
        }
        module(id: $moduleId) {
          id
          abbreviation
          nodes {
            id
            parentId
            isLibraryReference
            step {
              id
              name
              stepType {
                id
                name
              }
              durationInSeconds
              icons {
                id
                name
                imageUpload {
                  id
                  imageUrl(options: { width: 64, height: 64 })
                }
              }
              issueLinks @include(if: $includeIssues) {
                ...AssociatedIssueTableIssueLink
              }
            }
          }
        }
      }
    }

    ${associatedIssueTableIssueLinkFragment}
  `,
  () => ({
    bopId: props.bopId,
    moduleId: props.moduleId,
    includeIssues: hasOrgAuthz(PermissionOrganization.OrgIssueRead),
  }),
)

const isEditable = computed(
  () =>
    !isBopLocked(fetchQuery.result.value?.product.bop) &&
    hasOrgAuthz(PermissionOrganization.OrgProductWrite),
)
const abbreviation = computed(() => fetchQuery.result.value?.product.module.abbreviation || '')
const stepNodes = computed<LocalNode[]>(() => fetchQuery.result.value?.product.module.nodes || [])
const stepTreeNodes = computed<StepTreeStepNode[]>(() =>
  stepNodes.value
    .filter((n) => !n.parentId)
    .map((n, i) => createStepTreeNode(i, [abbreviation.value], n, [])),
)
function createStepTreeNode(
  index: number,
  parentNumbering: string[],
  node: LocalNode,
  previousNodeIds: string[],
): StepTreeStepNode {
  const ancestryNodeIds = [...previousNodeIds, node.id]
  const ancestryNumbering = [...parentNumbering, `${index + 1}`]
  const children = stepNodes.value
    .filter((n) => n.parentId == node.id)
    .map((n, i) => createStepTreeNode(i, ancestryNumbering, n, ancestryNodeIds))

  return {
    numbering: ancestryNumbering.join('.'),
    stepNode: node,
    children,
    ancestryNodeIds,
    isSelected: node.id == props.selectedNodeId,
  }
}

export type StepTiming = { start: number; span: number }
export type StepTimingMap = {
  [nodeId: string]: StepTiming
}
const stepTimings = computed<{ map: StepTimingMap; total: number }>(() => {
  const map: StepTimingMap = {}
  const total = assignTimings(stepTreeNodes.value, 0, map)
  return { map, total }
})
function assignTimings(nodes: StepTreeStepNode[], cursorStart: number, map: StepTimingMap): number {
  let cursor = cursorStart
  nodes.forEach((n) => {
    const start = cursor
    const own = n.children.length > 0 ? 0 : n.stepNode.step.durationInSeconds
    const end = assignTimings(n.children, start + own, map)
    map[n.stepNode.id] = { start, span: end - start }
    cursor = end
  })
  return cursor
}

const notifySaved = ref(false)
function stepNodesToGraphNodes(nodes: LocalNode[]): SetTreeGraphNode[] {
  return nodes.map((n) => ({
    id: n.id,
    parentId: n.parentId,
    entityType: GraphEntityType.Step,
    entityId: n.step.id,
  }))
}

const setGraphMutation = useMutation<Mutation, { graph: SetTreeGraph }>(gql`
  mutation setGraphForModule($graph: SetTreeGraph!) {
    product {
      setModuleGraph(command: $graph)
    }
  }
`)

function setGraph(nodes: SetTreeGraphNode[]) {
  setGraphMutation
    .mutate({
      graph: {
        entityId: props.moduleId,
        nodes,
      },
    })
    .then(() => {
      notifySaved.value = true
      fetchQuery.refetch()
    })
}
function setNodes(nodes: LocalNode[]) {
  setGraph(stepNodesToGraphNodes(nodes))
}

function getNode(nodeId: string) {
  return stepNodes.value.find((n) => n.id == nodeId) as LocalNode
}
function moveNode(movedNodeId: string, targetNodeId: string, after: boolean) {
  const currentIndex = stepNodes.value.findIndex((n) => n.id == movedNodeId)
  let targetIndex = stepNodes.value.findIndex((n) => n.id == targetNodeId)
  if (after) {
    targetIndex++
  }
  if (!after && currentIndex < targetIndex) {
    targetIndex--
  }

  getNode(movedNodeId).parentId = getNode(targetNodeId).parentId

  const newNodes = [...stepNodes.value]
  newNodes.splice(targetIndex, 0, newNodes.splice(currentIndex, 1)[0])
  setNodes(newNodes)
}
function moveBefore(movedNodeId: string, targetNodeId: string) {
  moveNode(movedNodeId, targetNodeId, false)
}
function moveAfter(movedNodeId: string, targetNodeId: string) {
  moveNode(movedNodeId, targetNodeId, true)
}

// Empty steps land as a sibling directly after the selected step, not at the tree bottom.
function addEmptyStep() {
  const selected = props.selectedNodeId
    ? stepNodes.value.find((n) => n.id == props.selectedNodeId)
    : undefined
  emit('createNode', selected?.parentId ?? undefined, selected?.id)
}
async function placeAfter(newStepId: string, afterNodeId: string) {
  await fetchQuery.refetch()
  const newNode = stepNodes.value.find((n) => n.step.id == newStepId)
  if (!newNode) {
    return
  }
  moveAfter(newNode.id, afterNodeId)
}
const dropDecisionDialog = ref<ComponentExposed<typeof StepDropDecisionDialog>>()
const { createStepNode } = useCreateStepNode()
// Nesting onto a filled leaf orphans its data, so the user decides what happens to it first.
async function makeFirstChild(movedNodeId: string, targetNodeId: string) {
  const movedNode = getNode(movedNodeId)
  const targetNode = getNode(targetNodeId)
  const targetHasChildren = stepNodes.value.some((n) => n.parentId == targetNodeId)

  let decision: DropDecision | undefined = { action: 'nest' }
  if (!targetHasChildren) {
    decision = await dropDecisionDialog.value?.open(
      { stepId: movedNode.step.id, isLibraryReference: movedNode.isLibraryReference },
      { stepId: targetNode.step.id, isLibraryReference: targetNode.isLibraryReference },
    )
  }
  if (!decision) {
    return
  }

  if (decision.action == 'bundle') {
    const created = await createStepNode({
      productId: props.productId,
      moduleId: props.moduleId,
      name: decision.name,
      stepTypeId: decision.stepTypeId,
    })
    wrapNodesInSection(created.nodeId, created.stepId, [targetNodeId, movedNodeId])
    return
  }

  getNode(movedNodeId).parentId = targetNodeId

  const newNodes = [...stepNodes.value]
  const [moved] = newNodes.splice(
    newNodes.findIndex((n) => n.id == movedNodeId),
    1,
  )
  const firstChildIndex = newNodes.findIndex((n) => n.parentId == targetNodeId)
  const insertIndex =
    firstChildIndex >= 0 ? firstChildIndex : newNodes.findIndex((n) => n.id == targetNodeId) + 1
  newNodes.splice(insertIndex, 0, moved)
  setNodes(newNodes)
}

const selectedNodeIds = ref(new Set<string>())
function toggleSelect(nodeId: string) {
  // Starting a multi-selection includes the step already shown in the panel, like file managers do.
  if (selectedNodeIds.value.size == 0 && props.selectedNodeId) {
    if (props.selectedNodeId != nodeId) {
      selectedNodeIds.value.add(props.selectedNodeId)
    }
  }

  if (selectedNodeIds.value.has(nodeId)) {
    selectedNodeIds.value.delete(nodeId)
  } else {
    selectedNodeIds.value.add(nodeId)
  }
}
function selectStep(nodeId: string) {
  selectedNodeIds.value.clear()
  emit('selectNodeId', nodeId)
}

// Session-local: lets a freshly created section render as one before it has children.
const emptySectionNodeIds = ref(new Set<string>())

const selectedSectionNode = computed(() => {
  const node = stepNodes.value.find((n) => n.id == props.selectedNodeId)
  if (!node) {
    return undefined
  }

  const hasChildren = stepNodes.value.some((n) => n.parentId == node.id)
  return hasChildren || emptySectionNodeIds.value.has(node.id) ? node : undefined
})

const sectionDialog = ref<ComponentExposed<typeof CreateSectionDialog>>()
async function addSection() {
  const created = await sectionDialog.value?.open()
  if (!created) {
    return
  }

  emptySectionNodeIds.value.add(created.nodeId)
  fetchQuery.refetch()
}
async function groupIntoSection() {
  const created = await sectionDialog.value?.open()
  if (!created) {
    return
  }

  wrapNodesInSection(created.nodeId, created.stepId, [...selectedNodeIds.value])
  selectedNodeIds.value.clear()
}

function hasAncestorIn(node: LocalNode, ids: Set<string>): boolean {
  let parentId = node.parentId
  while (parentId) {
    if (ids.has(parentId)) {
      return true
    }
    parentId = stepNodes.value.find((n) => n.id == parentId)?.parentId
  }
  return false
}
// Selected nodes become children of the new section, which takes the topmost member's place.
function wrapNodesInSection(sectionNodeId: string, sectionStepId: string, memberNodeIds: string[]) {
  const selected = new Set(memberNodeIds)
  const members = stepNodes.value.filter((n) => selected.has(n.id) && !hasAncestorIn(n, selected))
  if (members.length == 0) {
    return
  }

  const graphNodes: SetTreeGraphNode[] = []
  stepNodes.value.forEach((n) => {
    if (n.id == members[0].id) {
      graphNodes.push({
        id: sectionNodeId,
        parentId: members[0].parentId,
        entityType: GraphEntityType.Step,
        entityId: sectionStepId,
      })
    }
    graphNodes.push({
      id: n.id,
      parentId: members.some((m) => m.id == n.id) ? sectionNodeId : n.parentId,
      entityType: GraphEntityType.Step,
      entityId: n.step.id,
    })
  })
  setGraph(graphNodes)
}

defineExpose({
  refresh: fetchQuery.refetch,
  firstNodeId: () => stepTreeNodes.value[0]?.stepNode.id as string | undefined,
  placeAfter,
})
</script>

<template>
  <h2 class="mb-2" style="position: relative">
    {{ t('component.stepTree.title') }}
    <v-progress-circular
      v-if="fetchQuery.loading.value || setGraphMutation.loading.value"
      indeterminate
      size="20"
    />
    <v-btn-group class="float-right" density="compact">
      <v-btn
        v-if="isEditable && selectedNodeIds.size > 0"
        prepend-icon="create_new_folder"
        @click="groupIntoSection"
      >
        {{ t('component.stepTree.buttonGroupIntoSection', { count: selectedNodeIds.size }) }}
      </v-btn>
      <v-menu v-if="isEditable">
        <template #activator="{ props: activatorProps }">
          <v-btn
            icon="add_circle"
            :title="t('component.stepTree.buttonAdd')"
            v-bind="activatorProps"
          />
        </template>
        <v-list density="compact">
          <v-list-item @click="addEmptyStep">
            <v-icon icon="add" size="small" class="me-3" />
            {{ t('component.stepTree.buttonAddEmpty') }}
          </v-list-item>
          <v-list-item
            v-if="selectedSectionNode"
            @click="$emit('createNode', selectedSectionNode.id)"
          >
            <v-icon icon="subdirectory_arrow_right" size="small" class="me-3" />
            {{
              t('component.stepTree.buttonAddInSection', { section: selectedSectionNode.step.name })
            }}
          </v-list-item>
          <v-list-item @click="addSection">
            <v-icon icon="create_new_folder" size="small" class="me-3" />
            {{ t('component.stepTree.buttonAddSection') }}
          </v-list-item>
          <v-list-item @click="$emit('addFromLibrary')">
            <v-icon icon="library_add_check" size="small" class="me-3" />
            {{ t('component.stepTree.buttonAddFromLibrary') }}
          </v-list-item>
        </v-list>
      </v-menu>
      <v-btn
        v-if="isEditable"
        icon="content_copy"
        :title="t('component.stepTree.buttonCopy')"
        @click="$emit('copyNode')"
      />
      <v-btn
        v-if="isEditable"
        icon="auto_awesome"
        :title="t('component.stepTree.buttonProposeFromFile')"
        @click="$emit('proposeFromMedia')"
      />
      <v-btn
        icon="visibility"
        :title="t('component.stepTree.buttonPreview')"
        @click="$emit('preview')"
      />
    </v-btn-group>
  </h2>

  <div
    v-if="stepTreeNodes.length == 0 && !fetchQuery.loading.value && isEditable"
    class="emptyState"
  >
    <v-btn color="primary" @click="$emit('proposeFromMedia')">
      {{ t('component.stepTree.buttonProposeFromFile') }}
      <v-icon class="ml-1" icon="auto_awesome" />
    </v-btn>
    <v-btn @click="$emit('copyNode')">
      {{ t('component.stepTree.buttonCopy') }} <v-icon class="ml-1" icon="content_copy" />
    </v-btn>
    <v-btn @click="$emit('addFromLibrary')">
      {{ t('component.stepTree.buttonAddFromLibrary') }}
      <v-icon class="ml-1" icon="library_add_check" />
    </v-btn>
    <v-btn @click="$emit('createNode')">
      {{ t('component.stepTree.buttonCreate') }} <v-icon class="ml-1" icon="add_circle" />
    </v-btn>
  </div>

  <step-tree-node
    v-for="(n, i) in stepTreeNodes"
    :key="n.stepNode.id"
    :drag-drop-disabled="!isEditable"
    :node="n"
    :depth="0"
    :step-timings="stepTimings.map"
    :module-total-duration="stepTimings.total"
    :module-id="props.moduleId"
    :selected-node-ids="selectedNodeIds"
    :empty-section-node-ids="emptySectionNodeIds"
    :closing-nodes="[]"
    :next-sibling-node-id="stepTreeNodes[i + 1]?.stepNode.id"
    :is-first-root="i == 0"
    @move-before="moveBefore"
    @move-after="moveAfter"
    @make-first-child="makeFirstChild"
    @toggle-select="toggleSelect"
    @select-node-id="selectStep"
  />

  <create-section-dialog
    ref="sectionDialog"
    :product-id="props.productId"
    :module-id="props.moduleId"
  />
  <step-drop-decision-dialog ref="dropDecisionDialog" :module-id="props.moduleId" />
</template>

<style scoped lang="scss">
.emptyState {
  position: relative;
  text-align: center;
  margin-top: 2em;

  button {
    display: block !important;
    width: 25em;
    margin-bottom: 0.5em;
    margin-left: auto;
    margin-right: auto;
  }
}
</style>
